/**
 * Interface commune pour tous les adapters de balance
 * Permet une architecture pluggable pour supporter différents modèles
 */

export interface WeightReading {
  weight: number; // Poids en grammes
  stable: boolean; // true si la mesure est stable
  unit: 'g' | 'kg'; // Unité de mesure
  timestamp: number; // Timestamp de la lecture
}

export interface ScaleConfig {
  model: 'BAXTRAN_SL' | 'MANUAL' | 'BIZERBA' | 'METTLER_TOLEDO';
  autoConnect?: boolean;
  pollInterval?: number; // Intervalle de polling en ms (pour certains modèles)
  port?: string; // Port série (pour WebSocket adapter)
}

export interface ScaleAdapter {
  /**
   * Nom du modèle de balance
   */
  readonly modelName: string;

  /**
   * Indique si l'adapter supporte la connexion automatique
   */
  readonly supportsAutoConnect: boolean;

  /**
   * Connecter la balance
   * @throws Error si la connexion échoue
   */
  connect(): Promise<void>;

  /**
   * Déconnecter la balance
   */
  disconnect(): Promise<void>;

  /**
   * Vérifier si la balance est connectée
   */
  isConnected(): boolean;

  /**
   * Lire le poids actuel
   * @throws Error si la balance n'est pas connectée
   */
  readWeight(): Promise<WeightReading>;

  /**
   * Tarrer la balance (remettre à zéro)
   * @throws Error si la balance ne supporte pas cette fonctionnalité
   */
  tare(): Promise<void>;

  /**
   * S'abonner aux changements de poids
   * @param callback Fonction appelée à chaque nouveau poids
   * @returns Fonction de désabonnement
   */
  subscribe(callback: (reading: WeightReading) => void): () => void;
}

/**
 * Erreurs spécifiques aux balances
 */
export class ScaleError extends Error {
  constructor(
    message: string,
    public code: 'NOT_SUPPORTED' | 'NOT_CONNECTED' | 'READ_ERROR' | 'CONNECTION_ERROR',
    public originalError?: Error
  ) {
    super(message);
    this.name = 'ScaleError';
  }
}
